// Floors panel rendered inline under SiteDetailPage. Has its own
// PageHeader (a sub-section heading) so the Create CTA gets a home;
// the parent SiteLayout already owns the global title.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { floors } from "../../api/endpoints/floors";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Table } from "../../components/ui/Table";
import type { FloorOut } from "../../api/types/floor";

import { FloorCreateForm } from "./FloorCreateForm";

interface FloorListPanelProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
}

export function FloorListPanel({
  accountSlug,
  tenantSlug,
  siteSlug,
}: FloorListPanelProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.floors(accountSlug, tenantSlug, siteSlug),
    queryFn: () =>
      floors.list(accountSlug, tenantSlug, siteSlug).then((r) => r.data),
  });

  return (
    <>
      <header style={panelHeader}>
        <h3 style={panelHeading}>Floors</h3>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          style={primaryBtn}
        >
          Create floor
        </button>
      </header>

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list floors" />}
      {q.data && (
        <Table<FloorOut>
          columns={[
            {
              key: "slug",
              header: "Slug",
              render: (f) => (
                <Link
                  to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/floors/$floorSlug"
                  params={{ accountSlug, tenantSlug, siteSlug, floorSlug: f.slug }}
                  style={{ color: "var(--accent)" }}
                >
                  {f.slug}
                </Link>
              ),
            },
            { key: "label", header: "Label" },
            { key: "level", header: "Level", align: "right" },
          ]}
          rows={q.data}
          rowKey={(f) => f.id}
          empty={
            <EmptyState
              title="No floors on this site"
              description="Add ground floor first, then basement / upper floors with their level offsets."
            />
          }
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create floor on ${siteSlug}`}
        width={480}
      >
        <FloorCreateForm
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          siteSlug={siteSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const panelHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
};

const panelHeading: React.CSSProperties = {
  margin: 0,
  fontSize: "0.95rem",
  color: "var(--text-dim)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "6px 12px",
  borderRadius: 4,
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
};
